import { ButtonLink, Flex, Pagination, Table, TableColumn, TableColumnSort, Tag, Text } from '@applyboard/crystal-ui';
import { useEffect, useMemo, useState } from 'react';
import { AgencyData, ProfileData } from '../../clients/types';
import { LoadingPage } from '../../pages/LoadingPage';
import { useGetAllAgencies } from '../../hooks/agencies/useGetAllAgencies';
import { EyeOutlineIcon } from '@applyboard/ui-icons';

const FilterableKeys = ['name', 'enabled'] as const;
type FilterableKey = (typeof FilterableKeys)[number];

const PAGE_SIZE = 10;

type AgencyManagementTableProps = Readonly<{
  currentSessionUser: ProfileData | undefined;
  isGetProfileLoading: boolean;
  filter: string;
  search: string;
}>;

export function AgencyManagementTable({
  currentSessionUser,
  isGetProfileLoading,
  filter,
  search,
}: AgencyManagementTableProps): JSX.Element {
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<TableColumnSort>([
    {
      desc: false,
      id: 'name',
    },
  ]);

  const { allAgenciesData, isAgenciesLoading } = useGetAllAgencies();

  const currentSessionUserIsResolved = !isGetProfileLoading && currentSessionUser;

  // Convert AgencyResource to AgencyData (memoized to avoid re-creating on every render)
  const agencyData = useMemo(
    () =>
      allAgenciesData?.data?.map((agency) => ({
        id: agency.id,
        name: agency.attributes.name,
        enabled: agency.attributes.enabled,
      })),
    [allAgenciesData]
  );

  const [data, setData] = useState<Array<AgencyData> | undefined>(agencyData);

  const applySorting = (dataToSort: Array<AgencyData>, sortState: TableColumnSort) => {
    if (sortState.length === 0) return dataToSort;

    const sortedData = dataToSort.slice();
    sortedData.sort((a, b) => sortAgencyData(sortState, a, b));
    return sortedData;
  };

  useEffect(() => {
    if (agencyData) {
      const filteredData = getFilteredData(agencyData, filter, search);
      const sortedData = applySorting(filteredData, sorting);
      setData(sortedData);
    }
  }, [agencyData, filter, search, sorting]);

  const columns = useMemo<TableColumn<AgencyData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Agency Name',
        enableSorting: true,
        cell: (agency: AgencyData) => {
          return (
            <Table.Cell>
              <Text>{agency.name}</Text>
            </Table.Cell>
          );
        },
      },
      {
        accessorKey: 'enabled',
        header: 'Status',
        enableSorting: true,
        cell: (agency: AgencyData) => (
          <Table.Cell>
            <Tag intent={agency.enabled ? 'positive' : 'negative'}>{agency.enabled ? 'Enabled' : 'Disabled'}</Tag>
          </Table.Cell>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: (agency: AgencyData) =>
          currentSessionUserIsResolved ? (
            <Table.Cell>
              <ButtonLink
                variant="transparent"
                href={`/profiles/agencies/${agency.id}`}
                aria-label="Edit Agency"
                leadIcon={EyeOutlineIcon}
              />
            </Table.Cell>
          ) : (
            <LoadingPage />
          ),
      },
    ],
    [currentSessionUser?.id, currentSessionUser?.userType, currentSessionUserIsResolved]
  );

  return (
    <>
      <Flex pt={{ xs: 2, sm: 8 }} direction="column" justify="center">
        <Table
          loading={isAgenciesLoading}
          columns={columns}
          data={data?.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) ?? []}
          onSort={(newSorting) => {
            // This triggers the useEffect which will apply the sorting to the data
            setSorting(newSorting);
          }}
          sort={sorting}
        >
          <></>
        </Table>
      </Flex>
      <Flex pt={{ xs: 2, sm: 8 }} justify="start" align="center" basis="100%">
        {!isAgenciesLoading && (
          <Pagination
            currentPage={page + 1}
            numItems={Math.min(agencyData?.length ?? 0, data?.length ?? 0)}
            onPageChange={(page) => setPage(page - 1)}
            pageSize={PAGE_SIZE}
          />
        )}
      </Flex>
    </>
  );
}

export function sortAgencyData(sorting: TableColumnSort, a: AgencyData, b: AgencyData) {
  if (!a || !b) {
    return 0;
  }

  if (sorting[0].id === 'id') {
    return (sorting[0].desc ? -1 : 1) * a.id.localeCompare(b.id);
  }

  if (sorting[0].id === 'enabled') {
    const enabledA = a.enabled ? 'enabled' : 'disabled';
    const enabledB = b.enabled ? 'enabled' : 'disabled';
    return (sorting[0].desc ? -1 : 1) * enabledA.localeCompare(enabledB);
  }

  const sortKey1 = sorting[0].id as FilterableKey;
  const valueA1 = getNestedAgencyValue(a, sortKey1);
  const valueB1 = getNestedAgencyValue(b, sortKey1);

  const sortResult1 = (sorting[0].desc ? -1 : 1) * valueA1.localeCompare(valueB1);
  if (sortResult1 !== 0 || sorting.length === 1) {
    return sortResult1;
  }

  if (sorting[1].id === 'enabled') {
    const enabledA2 = a.enabled ? 'enabled' : 'disabled';
    const enabledB2 = b.enabled ? 'enabled' : 'disabled';
    return (sorting[1].desc ? -1 : 1) * enabledA2.localeCompare(enabledB2);
  }

  const sortKey2 = sorting[1].id as FilterableKey;
  const valueA2 = getNestedAgencyValue(a, sortKey2);
  const valueB2 = getNestedAgencyValue(b, sortKey2);

  return (sorting[1].desc ? -1 : 1) * valueA2.localeCompare(valueB2);
}

function getFilteredData(allAgencyData: Array<AgencyData>, filter: string, search: string): Array<AgencyData> {
  if (filter !== '' && search !== '') {
    const agencyFilter = filter as FilterableKey;

    const filteredAgencies = allAgencyData.filter((agency) => {
      const value = getNestedAgencyValue(agency, agencyFilter);
      return value.toLowerCase().includes(search.toLowerCase());
    });

    return filteredAgencies;
  }

  return allAgencyData;
}

export function getNestedAgencyValue(agency: AgencyData, key: FilterableKey): string {
  switch (key) {
    case 'name':
      return agency.name || '';
    case 'enabled':
      return agency.enabled ? 'Enabled' : 'Disabled';
    default:
      throw new Error(`Unknown key: ${key}`);
  }
}
